import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { webPage, breadcrumb } from "@/lib/jsonLd";
import { findTraining, STOIC_TRAINING, TRAINING_LABELS } from "@/data/stoicTraining";
import { TRAINING_I18N_A } from "@/data/stoicTrainingI18nA";
import { TRAINING_I18N_B } from "@/data/stoicTrainingI18nB";
import { pickLang } from "@/data/stoicosCards";
import { useT } from "@/i18n/LanguageContext";

export default function StoicTrainingPage() {
  const { slug } = useParams();
  const { lang } = useT();
  const base = findTraining(slug);

  /* Localised rendering: NL/DE/FR/ES override intro/steps/mantra/note;
     SEO, canonical, prerender and JSON-LD stay EN by architecture. */
  const tr = (base && lang !== "en" && (TRAINING_I18N_A[slug]?.[lang] || TRAINING_I18N_B[slug]?.[lang])) || null;
  const c = base ? { ...base, ...(tr || {}) } : null;
  const labels = TRAINING_LABELS[lang] || TRAINING_LABELS.en;
  const title = c ? pickLang(c.title, lang) : "";
  const partLabel = c ? pickLang(c.partLabel, lang) : "";

  useSEO({
    title: base ? (lang === "en" ? base.metaTitle : `${title} | StoicOs.ai`) : "Stoic Training | StoicOs.ai",
    description: base ? base.metaDescription : "",
    canonical: base ? `https://stoicos.ai/stoic-training/${base.slug}` : "https://stoicos.ai/stoic-training-cards",
    og: base ? { title: base.metaTitle, description: base.metaDescription, image: `https://stoicos.ai/cards/${base.file}`, type: "article" } : undefined,
    jsonLd: base ? [
      webPage({ name: `Stoic Training ${base.partLabel.en}: ${base.title.en}`, description: base.metaDescription, url: `/stoic-training/${base.slug}` }),
      breadcrumb([
        { name: "Home", url: "/" },
        { name: "StoicOs Visual Library", url: "/stoic-training-cards" },
        { name: `${base.partLabel.en} — ${base.title.en}`, url: `/stoic-training/${base.slug}` },
      ]),
    ] : [],
  });

  if (!c) return <Navigate to="/stoic-training-cards" replace />;

  const prev = STOIC_TRAINING.find((x) => x.order === c.order - 1);
  const next = STOIC_TRAINING.find((x) => x.order === c.order + 1);
  const numbered = c.steps.some((s) => s.t);

  return (
    <main data-testid="training-page" className="relative pt-32 md:pt-40 pb-32">
      <article className="max-w-3xl mx-auto px-6 md:px-10">
        <Link to="/stoic-training-cards" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-fog/70 hover:text-gold transition-colors mb-12" data-testid="training-back">
          <ArrowLeft size={12} strokeWidth={1.5} /> StoicOs Visual Library
        </Link>

        <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">{labels.eyebrow} · {partLabel}</p>
        <h1 data-testid="training-title" className="font-serif text-4xl sm:text-5xl text-bone tracking-tight leading-[1.08]">{title}</h1>
        <p className="mt-4 font-serif italic text-xl md:text-2xl text-gold/90 tracking-tight leading-snug">{pickLang(c.tagline, lang)}</p>
        <p className="mt-8 text-bone/90 font-reading text-lg md:text-xl leading-[1.85]" data-testid="training-intro">{c.intro}</p>

        <figure className="mt-12">
          <img src={`/cards/${c.file}`} alt={`${partLabel} — ${title} · Ed Korporaal, Stoic Wisdom`} width={c.w} height={c.h} loading="eager" className="w-full h-auto border border-white/10" />
          <figcaption className="mt-3 text-[11px] tracking-[0.18em] uppercase text-fog/60">{partLabel} — {title} · Ed Korporaal, Stoic Wisdom</figcaption>
        </figure>

        <section className="mt-14" data-testid="training-steps">
          <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">{labels.training}</h2>
          {numbered ? (
            <ol className="space-y-8">
              {c.steps.map((s, i) => (
                <li key={i} className="pl-5 border-l border-gold/40">
                  <h3 className="font-serif text-xl text-bone mb-2">{i + 1}. {s.t}</h3>
                  <p className="text-bone/85 font-reading text-base md:text-lg leading-[1.8]">{s.d}</p>
                </li>
              ))}
            </ol>
          ) : (
            <div className="space-y-6">
              {c.steps.map((s, i) => (
                <p key={i} className="text-bone/85 font-reading text-base md:text-lg leading-[1.85]">{s.d}</p>
              ))}
            </div>
          )}
        </section>

        <section className="mt-14">
          <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold mb-5">{labels.mantra}</h2>
          <blockquote data-testid="training-mantra" className="p-8 md:p-10 border border-gold/25 bg-ink-elevated/40 font-serif text-xl md:text-2xl text-bone tracking-tight leading-relaxed">
            {c.mantra}
          </blockquote>
        </section>

        {c.note && (
          <section className="mt-14" data-testid="training-note">
            <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold mb-5">{labels.note}</h2>
            <p className="p-6 border border-white/10 bg-ink-elevated/30 text-fog/85 font-reading text-sm md:text-base leading-[1.8]">{c.note}</p>
          </section>
        )}

        <section className="mt-14">
          <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold mb-5">{labels.deeper}</h2>
          <ul className="space-y-2">
            {c.links.map((l, i) => (
              <li key={i}>
                <Link to={l.to} className="text-gold hover:underline font-reading text-base md:text-lg" data-testid={`training-link-${i}`}>{pickLang(l.label, lang)} →</Link>
              </li>
            ))}
          </ul>
        </section>

        <nav className="mt-16 pt-8 border-t border-white/10 flex flex-wrap justify-between gap-4" aria-label={labels.series}>
          {prev ? (
            <Link to={`/stoic-training/${prev.slug}`} className="text-[11px] tracking-[0.22em] uppercase text-fog/70 hover:text-gold transition-colors" data-testid="training-prev">
              ← {labels.prev}: {pickLang(prev.title, lang)}
            </Link>
          ) : <span />}
          {next && (
            <Link to={`/stoic-training/${next.slug}`} className="text-[11px] tracking-[0.22em] uppercase text-fog/70 hover:text-gold transition-colors text-right" data-testid="training-next">
              {labels.next}: {pickLang(next.title, lang)} →
            </Link>
          )}
        </nav>

        <p className="mt-10 text-[11px] tracking-[0.2em] uppercase text-fog/60">{labels.by}</p>
      </article>
    </main>
  );
}
